// Minden összeg éves, ezer Ft-ban (eFt).

// 2026-os értékek, a 2027-es összegek még nincsenek kihirdetve
export const MINIMALBER = 322.8;
export const BERMINIMUM = 373.2;
// 25 év alattiak kedvezményének havi felső határa
const SZJA_MENTES_HAVI = 715.765;
// főállásban havi 100 eFt, mellékállásban (és nyugdíjasként) havi 50 eFt
const KATA_HAVI = 100;
const KATA_HAVI_MELLEKALLASU = 50;
export const KATA_LIMIT = 22_000;

// átalányadózás bevételi korlátja: az éves minimálbér 10-szerese (90%-os költséghányadnál 50-szerese)
export const atalanyLimit = (koltseghanyad, minimalber = MINIMALBER) =>
  koltseghanyad === 0.9 ? minimalber * 600 : minimalber * 120;

// sávos (egyszerűsített) IPA-alap 25 M Ft éves bevételig, Htv. 39/A. §
const calculateIPASavos = (income, ipaKulcs) =>
  income <= 12000
    ? 2500 * ipaKulcs
    : income <= 18000
    ? 6000 * ipaKulcs
    : income <= 25000
    ? 8500 * ipaKulcs
    : Infinity;

const calculateIPAKATA = (income, expense, ipaKulcs) =>
  Math.min(2500 * ipaKulcs, Math.max(income - expense, 0) * ipaKulcs);

const calculateIPAAtalany = (income, koltseghanyad, ipaKulcs) =>
  income * (1 - koltseghanyad) * 1.2 * ipaKulcs;

const calculateIPATeteles = (income, expense, ipaKulcs) =>
  Math.min(
    calculateIPASavos(income, ipaKulcs),
    Math.max(income - expense, 0) * ipaKulcs
  );

const calculateSZJA = (adoalap, szjaMentes) =>
  Math.max((adoalap - (szjaMentes ? SZJA_MENTES_HAVI * 12 : 0)) * 0.15, 0);

// főállásban legalább a minimálbér (garantált bérminimum) után kell járulékot fizetni
const calculateTB = (adoalap, minimalber, foallasu, nyugdijas) => {
  if (nyugdijas) return 0;
  return foallasu
    ? Math.max(adoalap * 0.185, minimalber * 12 * 0.185)
    : adoalap * 0.185;
};
const calculateSZOCHO = (adoalap, minimalber, foallasu, nyugdijas) => {
  if (nyugdijas) return 0;
  return foallasu
    ? Math.max(adoalap * 0.13, minimalber * 12 * 0.13)
    : adoalap * 0.13;
};

// minimalber: a havi minimálbér; jarulekMinimum: a főállású járulékalap havi
// minimuma (minimálbér vagy garantált bérminimum)
export const computeTaxes = ({
  income,
  expense,
  koltseghanyad,
  minimalber = MINIMALBER,
  jarulekMinimum = minimalber,
  // a települési iparűzési adó mértéke, legfeljebb 2%
  ipaKulcs = 0.02,
  allastipus,
  szjaMentes,
  cegesJovedelem,
}) => {
  const foallasu = allastipus === 'foallasu';
  const nyugdijas = allastipus === 'nyugdijas';
  // SZOCHO felső határ: a minimálbér 24-szerese
  const szochoPlafon = minimalber * 24;

  // KATA
  const kataFix = (foallasu ? KATA_HAVI : KATA_HAVI_MELLEKALLASU) * 12;
  const kataOverIncome = Math.max(0, (income - KATA_LIMIT) * 0.4);
  // céges jövedelem: 15%, amibe a havi fix KATA beszámít
  const kataCeges = cegesJovedelem ? Math.max(0, income * 0.15 - kataFix) : 0;
  const kataIpa = calculateIPAKATA(income, expense, ipaKulcs);
  const kata = {
    kata: kataFix,
    overIncome: kataOverIncome,
    ceges: kataCeges,
    ipa: kataIpa,
    total: kataFix + kataOverIncome + kataCeges + kataIpa,
  };

  // Átalányadózás
  const atalanyJovedelem = income * (1 - koltseghanyad);
  // adómentes az éves minimálbér fele
  const mentesitett = Math.min(minimalber * 6, atalanyJovedelem);
  const atalanyAdoalap = Math.max(atalanyJovedelem - mentesitett, 0);
  const atalanyIpa = calculateIPAAtalany(income, koltseghanyad, ipaKulcs);
  const atalanySzja = calculateSZJA(atalanyAdoalap, szjaMentes);
  const atalanyTb = calculateTB(atalanyAdoalap, jarulekMinimum, foallasu, nyugdijas);
  const atalanySzocho = calculateSZOCHO(
    atalanyAdoalap,
    jarulekMinimum,
    foallasu,
    nyugdijas
  );
  const atalany = {
    available: income <= atalanyLimit(koltseghanyad, minimalber),
    koltseghanyad: income * koltseghanyad,
    jovedelem: atalanyJovedelem,
    mentesitett,
    adoalap: atalanyAdoalap,
    ipa: atalanyIpa,
    szja: atalanySzja,
    tb: atalanyTb,
    szocho: atalanySzocho,
    total: atalanySzja + atalanyTb + atalanySzocho + atalanyIpa,
  };

  // Tételes költségelszámolás, vállalkozói kivét nélkül
  const tetelesIpa = calculateIPATeteles(income, expense, ipaKulcs);
  const tetelesTb = calculateTB(0, jarulekMinimum, foallasu, nyugdijas);
  const tetelesSzochoJarulek = calculateSZOCHO(0, jarulekMinimum, foallasu, nyugdijas);
  // vállalkozói adóalap: a saját TB járulék nem költség, a SZOCHO és az IPA igen
  const tetelesAdoalap = Math.max(
    income - expense - tetelesSzochoJarulek - tetelesIpa,
    0
  );
  const vszja = tetelesAdoalap * 0.09;
  // vállalkozói osztalékalap: 15% SZJA, a 25 év alattiak kedvezménye nem vonatkozik rá
  const osztalekalap = tetelesAdoalap - vszja;
  const tetelesSzja = osztalekalap * 0.15;
  const tetelesSzocho = Math.min(
    tetelesSzochoJarulek + (nyugdijas ? 0 : osztalekalap * 0.13),
    szochoPlafon * 0.13
  );
  const teteles = {
    jovedelem: income - expense,
    ipa: tetelesIpa,
    vszja,
    szja: tetelesSzja,
    tb: tetelesTb,
    szocho: tetelesSzocho,
    total: tetelesTb + vszja + tetelesSzja + tetelesSzocho + tetelesIpa,
  };

  // Kft: a teljes adózott eredményt osztalékként veszi ki a tulajdonos
  const kftIpa = calculateIPATeteles(income, expense, ipaKulcs);
  const kftAdoalap = Math.max(income - expense - kftIpa, 0);
  const tao = kftAdoalap * 0.09;
  const osztalek = kftAdoalap - tao;
  const kftSzja = osztalek * 0.15;
  const kftSzocho = nyugdijas ? 0 : Math.min(osztalek, szochoPlafon) * 0.13;
  const kft = {
    jovedelem: income - expense,
    ipa: kftIpa,
    tao,
    osztalek,
    szja: kftSzja,
    szocho: kftSzocho,
    total: kftIpa + tao + kftSzja + kftSzocho,
  };

  return { kata, atalany, teteles, kft };
};
